/**
 * Erkennungs-Kern für den ANKAUF (Fall 1: Zukauf von Händlern/Herstellern). Nimmt den Text einer
 * Händler-Rechnung bzw. Auftragsbestätigung (aus der PDF-Textebene, sonst OCR) und zieht die Felder
 * heraus, mit denen FORQ die Maschine (`createMachine`) und den Lieferanten (`saveSupplier`) anlegt.
 * KEINE Nummern (Maschinen-/Lieferantennummer sind serverseitig).
 *
 * An echten Belegen geschärft (CA Maschinen4you-Layout u. ä.): Händlerbelege listen MEHRERE
 * Positionen (Transport, Ladegerät, dann die Maschine). Die Maschinendaten werden deshalb auf den
 * Positionsblock mit „Hersteller:" gescopt, damit nicht der Transport-„Typ"/-„Preis" gegriffen wird.
 * Der Lieferant ist der Absender (Briefkopf) — die eigene Firma (der Käufer) wird ausgeschlossen.
 * Rein und testbar; was nicht sicher erkannt wird, bleibt leer und wird im Prüf-Fenster ergänzt.
 */

export interface RecognizedAcquisition {
  readonly manufacturer: string;
  readonly model: string;
  readonly serialNumber: string;
  readonly constructionYear: number | null;
  readonly purchasePrice: number | null;
  readonly capacity: number | null;   // Tragkraft in kg
  readonly drive: string;             // Elektro|Diesel|Treibgas|Benzin|Hybrid|Lithium-Ionen | ''
  readonly hours: number | null;      // Betriebsstunden
  readonly liftHeight: number | null; // Hubhöhe in mm
  readonly condition: string;         // neu|gebraucht|generalüberholt|defekt | ''
  readonly supplierName: string;
  readonly supplierVatId: string;
  readonly supplierStreet: string;
  readonly supplierPostalCode: string;
  readonly supplierCity: string;
  readonly invoiceNumber: string;
}

const BRANDS = [
  'Linde', 'Still', 'Jungheinrich', 'Toyota', 'Hyster', 'Yale', 'Clark', 'Kion', 'Doosan',
  'Nissan', 'Mitsubishi', 'Caterpillar', 'Hangcha', 'Baoli', 'Steinbock', 'Rocla', 'Atlet',
  'Cesab', 'Hako', 'Crown', 'Combilift', 'Manitou', 'Kalmar', 'Konecranes',
];

// Trennzeichen zwischen Label und Wert: beliebig viele „: . -" und Leerzeichen (z. B. „Serien-Nr.: ").
const SEP = '[:.\\-\\s]*';

function normalizeSpace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

/** Deutsche Zahl → number: „1.234,56" → 1234.56, „8.450" → 8450, „3,6" → 3.6. */
function deNumber(raw: string): number | null {
  const match = raw.match(/-?\d[\d.]*(?:,\d+)?/);
  if (!match) return null;
  const value = Number.parseFloat(match[0].replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(value) ? value : null;
}

/** Nächstes Token nach einem Label (Hersteller, Seriennummer, Rechnungsnummer). */
function labeledToken(text: string, labels: string): string {
  const match = text.match(new RegExp(`(?:${labels})${SEP}([A-Za-z0-9][A-Za-z0-9\\-/.]*)`, 'i'));
  return match ? match[1].trim() : '';
}

/** Rest der Zeile nach einem Label (Modell „EJE 220", Firmenname). */
function labeledLine(text: string, labels: string): string {
  const match = text.match(new RegExp(`(?:${labels})${SEP}([^\\n\\r]+)`, 'i'));
  return match ? normalizeSpace(match[1]) : '';
}

/** Segment nach einem Label (Rest der Zeile) — für anschließendes deNumber(); Einheiten in Klammern stören nicht. */
function labeledSegment(text: string, labels: string): string {
  const match = text.match(new RegExp(`(?:${labels})${SEP}([^\\n\\r]*)`, 'i'));
  return match ? match[1] : '';
}

/**
 * Der Positionsblock, der die Maschine beschreibt: Händlerbelege sind nach „Pos. N" gegliedert; die
 * Maschine ist der Block mit „Hersteller:". Ohne Positionsgliederung → der ganze Text.
 */
function machineBlockOf(text: string): string {
  const blocks = text.split(/(?=Pos\.?\s*\d)/);
  if (blocks.length > 1) {
    const machine = blocks.find((block) => /Hersteller|Fabrikat/i.test(block));
    if (machine) return machine;
  }
  return text;
}

function detectManufacturer(block: string): string {
  const labeled = labeledToken(block, 'Hersteller|Fabrikat|Marke');
  if (labeled) return labeled;
  for (const brand of BRANDS) {
    if (new RegExp(`\\b${brand}\\b`, 'i').test(block)) return brand;
  }
  return '';
}

function detectDrive(block: string): string {
  const lower = block.toLowerCase();
  if (/lithium|li-ion/.test(lower)) return 'Lithium-Ionen';
  if (/hybrid/.test(lower)) return 'Hybrid';
  if (/treibgas|flüssiggas|fluessiggas|\blpg\b|\bgas\b/.test(lower)) return 'Treibgas';
  if (/diesel/.test(lower)) return 'Diesel';
  if (/benzin/.test(lower)) return 'Benzin';
  if (/elektro|batterie|elektrisch|\bvolt\b/.test(lower)) return 'Elektro';
  return '';
}

function detectCondition(block: string): string {
  const lower = block.toLowerCase();
  if (/generalüberholt|generaluberholt|überholt|ueberholt/.test(lower)) return 'generalüberholt';
  if (/defekt/.test(lower)) return 'defekt';
  if (/gebraucht|vorführ|vorfuhr/.test(lower)) return 'gebraucht';
  if (/fabrikneu|neugerät|neugeraet|\bneu\b/.test(lower)) return 'neu';
  return '';
}

/** Tragkraft in kg — „2000", „2.500 kg" oder „1,6 t" (→ ×1000). Einheit in Klammern stört nicht. */
function detectCapacity(block: string): number | null {
  const segment = labeledSegment(block, 'Tragkraft|Hubkraft|Tragfähigkeit|Tragfaehigkeit|Nenntragfähigkeit|Nutzlast');
  if (!segment) return null;
  const tonnes = segment.match(/(\d+(?:[.,]\d+)?)\s*t\b/i);
  if (tonnes) { const value = deNumber(tonnes[1]); return value != null ? Math.round(value * 1000) : null; }
  return deNumber(segment);
}

/** Hubhöhe in mm — „200", „3.600 mm" oder „3,6 m" (→ ×1000). */
function detectLiftHeight(block: string): number | null {
  const segment = labeledSegment(block, 'Hubhöhe|Hubhoehe|Hub\\b');
  if (!segment) return null;
  const metres = segment.match(/(\d+(?:[.,]\d+)?)\s*m\b/i);
  if (metres) { const value = deNumber(metres[1]); return value != null ? Math.round(value * 1000) : null; }
  return deNumber(segment);
}

function detectYear(block: string): number | null {
  const match = block.match(/(?:Baujahr|Bj\.?|Erstzulassung|Fabrikationsjahr|Herstelljahr)\s*[:.\-]?\s*((?:19|20)\d{2})/i);
  return match ? Number.parseInt(match[1], 10) : null;
}

/** Betriebsstunden — auch „Betriebsstundenzählerstand" und „… Bh". */
function detectHours(block: string): number | null {
  const labeled = block.match(/(?:Betriebsstunden(?:zählerstand|zaehlerstand|zähler)?|Betr\.?-?Std\.?|Einsatzstunden)\s*[:.\-]?\s*([\d.]+)/i);
  if (labeled) return deNumber(labeled[1]);
  const suffixed = block.match(/([\d.]+)\s*(?:Bh|Betriebsstunden|Betriebsstd\.?)\b/i);
  return suffixed ? deNumber(suffixed[1]) : null;
}

/** Preis der Maschine (Positionspreis), sonst die Netto-Belegsumme. */
function detectPrice(block: string, full: string): number | null {
  const positionPrice = deNumber(labeledSegment(block, 'Kaufpreis|Ankaufpreis|Ankaufspreis|Einkaufspreis|\\bPreis\\b'));
  if (positionPrice != null && positionPrice > 0) return positionPrice;
  const netTotal = deNumber(labeledSegment(full, 'Summe Positionen excl\\.?\\s?Mwst|Gesamt netto|Nettobetrag|Rechnungsbetrag netto|Zwischensumme'));
  return netTotal ?? positionPrice;
}

/** Lieferant = Absender/Briefkopf; die eigene Firma (Käufer) wird ausgeschlossen. */
function detectSupplierName(full: string, ownCompanyName: string): string {
  const labeled = labeledLine(full, 'Lieferant|Verkäufer|Verkaeufer|Absender');
  if (labeled) return labeled;
  const ownToken = normalizeSpace(ownCompanyName).toLowerCase().split(' ')[0];
  const lines = full.split(/\r?\n/).map(normalizeSpace).filter((line) => line.length > 2);
  const candidate = lines.find((line) => !ownToken || !line.toLowerCase().includes(ownToken));
  return candidate ?? lines[0] ?? '';
}

/**
 * OCR-/PDF-Text einer Ankauf-Unterlage → strukturierte Erkennung. `ownCompanyName` ist die eigene
 * Firma (aus dem Firmenprofil), damit der Käufer nicht als Lieferant erkannt wird.
 */
export function recognizeAcquisition(text: string, ownCompanyName = ''): RecognizedAcquisition {
  const block = machineBlockOf(text);
  const vatMatch = text.match(/\bDE\s?\d{9}\b/i);
  const zipCity = text.match(/\b(\d{5})\s+([A-ZÄÖÜ][A-Za-zÄÖÜäöüß.\-]{2,30})/);
  const streetMatch = text.match(/([A-ZÄÖÜ][A-Za-zäöüß.\- ]*(?:straße|strasse|str\.|weg|allee|ring|platz|damm|gasse)\s*\d+[a-z]?)/i);

  return {
    manufacturer: detectManufacturer(block),
    model: labeledLine(block, 'Typ|Modell|Modellbezeichnung|Type|Baureihe'),
    serialNumber: labeledToken(block, 'Ser\\.?\\s?-?\\s?Nr|Serien-?Nr|Seriennummer|Fabrik-?Nr|Fabriknummer|Fahrgestell-?Nr|Chassis-?Nr|Ident-?Nr'),
    constructionYear: detectYear(block),
    purchasePrice: detectPrice(block, text),
    capacity: detectCapacity(block),
    drive: detectDrive(block),
    hours: detectHours(block),
    liftHeight: detectLiftHeight(block),
    condition: detectCondition(block),
    supplierName: detectSupplierName(text, ownCompanyName),
    supplierVatId: vatMatch ? vatMatch[0].replace(/\s/g, '').toUpperCase() : '',
    supplierStreet: streetMatch ? normalizeSpace(streetMatch[1]) : '',
    supplierPostalCode: zipCity ? zipCity[1] : '',
    supplierCity: zipCity ? normalizeSpace(zipCity[2]) : '',
    invoiceNumber: labeledToken(text, 'Rechnungs?\\.?-?\\s?Nr\\.?|Rechnungsnummer|Auftrags-?Nr|Beleg-?Nr|RG-?Nr'),
  };
}
